import React from "react";
import { TeamRecentGamesDisplay } from "./TeamRecentGamesDisplay";
import { SG7PredictionCard } from "./SG7PredictionCard";
import { usePredictions, useFetchPredictions } from "@/hooks/usePredictions";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, Activity, BarChart3, Clock, RefreshCw, Percent, AlertCircle, History, ShieldAlert, TrendingDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { PredictionStatsTable } from "./PredictionStatsTable";
import { KeyFactsCard } from "@/components/analysis/KeyFactsCard";

interface GamePredictionsProps {
  fixtureId: number | null;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  currentGameId?: string;
  leagueName?: string;
  leagueLogo?: string;
  leagueApiId?: number;
  matchDate?: string;
  gameStatus?: string;
  homeScore?: number | null;
  awayScore?: number | null;
  elapsed?: number | null;
  statusShort?: string | null;
  teamHomeId?: string;
  teamAwayId?: string;
  analysisData?: any;
}
export function GamePredictions({
  fixtureId,
  homeTeamName,
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo,
  currentGameId,
  leagueName,
  leagueLogo,
  leagueApiId,
  matchDate,
  gameStatus,
  homeScore,
  awayScore,
  elapsed,
  statusShort,
  teamHomeId,
  teamAwayId,
  analysisData,
}: GamePredictionsProps) {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    dataUpdatedAt,
    refetch
  } = usePredictions(fixtureId);
  const fetchMutation = useFetchPredictions();
  
  // ✅ HOOKS MUST BE AT THE TOP - before any returns
  const [autoGenerating, setAutoGenerating] = React.useState(false);
  
  const handleFetchPredictions = async () => {
    if (!fixtureId) return;
    try {
      const result = await fetchMutation.mutateAsync(fixtureId);
      if (result.success) {
        refetch();
      } else {
        toast.warning(result.message || 'Predictions not available yet');
      }
    } catch (error: any) {
      toast.error(`Erro: ${error.message}`);
    }
  };
  
  React.useEffect(() => {
    if (!data && !isLoading && fixtureId && !fetchMutation.isPending && !autoGenerating) {
      setAutoGenerating(true);
      handleFetchPredictions();
    }
  }, [data, isLoading, fixtureId]);

  if (isLoading) {
    return <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>;
  }

  if (!data || !data.prediction) {
    if (fetchMutation.isPending || autoGenerating) {
      return (
        <div className="text-center py-12">
          <div className="sketch-border bg-paper-sketch p-8 max-w-md mx-auto">
            <RefreshCw className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
            <h3 className="text-base md:text-sketch-md font-black mb-3">
              Generating prediction...
            </h3>
            <p className="text-sm text-gray-medium">
              Please wait while we generate the predictive analysis.
            </p>
          </div>
        </div>
      );
    }

    // Fallback if auto-generation fails
    return (
      <div className="text-center py-12">
        <div className="sketch-border bg-paper-sketch p-8 max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-medium" />
          <h3 className="text-base md:text-sketch-md font-black mb-3">
            Prediction not available
          </h3>
          <p className="text-sm text-gray-medium mb-6">
            Unable to generate prediction automatically.
          </p>
          <Button onClick={handleFetchPredictions} disabled={fetchMutation.isPending} className="sketch-border hover-sketch">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try again
          </Button>
        </div>
      </div>
    );
  }
  const {
    prediction,
    teamStats,
    comparison,
    h2h
  } = data;

  // Parse percentages
  const homePercent = parseFloat(prediction.percent_home || '0');
  const drawPercent = parseFloat(prediction.percent_draw || '0');
  const awayPercent = parseFloat(prediction.percent_away || '0');

  // Winner badge color
  const getWinnerColor = (winner: string | null) => {
    if (winner?.toLowerCase().includes(homeTeamName.toLowerCase())) return 'default';
    if (winner?.toLowerCase().includes(awayTeamName.toLowerCase())) return 'secondary';
    return 'outline';
  };

  // Format form string
  // Render form as colored boxes (W=Win, D=Draw, L=Loss)
  const renderFormBoxes = (form: string | null) => {
    if (!form) return <span className="text-muted-foreground">N/A</span>;
    
    return (
      <div className="flex gap-1">
        {form.split('').map((char, index) => {
          let bgColor = 'bg-muted';
          let textColor = 'text-muted-foreground';
          
          if (char === 'W') {
            bgColor = 'bg-green-500';
            textColor = 'text-white';
          } else if (char === 'D') {
            bgColor = 'bg-yellow-400';
            textColor = 'text-gray-900';
          } else if (char === 'L') {
            bgColor = 'bg-red-500';
            textColor = 'text-white';
          }
          
          return (
            <div
              key={index}
              className={`w-6 h-6 flex items-center justify-center ${bgColor} ${textColor} font-bold text-xs rounded`}
            >
              {char}
            </div>
          );
        })}
      </div>
    );
  };

  // Parse goals by minute
  const parseGoalsByMinute = (data: any) => {
    if (!data) return [];
    const intervals = ['0-15', '16-30', '31-45', '46-60', '61-75', '76-90'];
    return intervals.map(interval => ({
      interval,
      goals: data[interval] || 0
    }));
  };
  const homeGoalsByMinute = parseGoalsByMinute(teamStats.home?.goals_for_by_minute);
  const awayGoalsByMinute = parseGoalsByMinute(teamStats.away?.goals_for_by_minute);

  // Parse goals by interval (marcados + sofridos)
  const parseGoalsByInterval = (homeStats: any, awayStats: any) => {
    if (!homeStats || !awayStats) return {
      home: [],
      away: []
    };
    const intervals = ['0-15', '16-30', '31-45', '46-60', '61-75', '76-90'];
    return {
      home: intervals.map(interval => ({
        interval,
        goalsFor: homeStats.goals_for_by_minute?.[interval] || 0,
        goalsAgainst: homeStats.goals_against_by_minute?.[interval] || 0
      })),
      away: intervals.map(interval => ({
        interval,
        goalsFor: awayStats.goals_for_by_minute?.[interval] || 0,
        goalsAgainst: awayStats.goals_against_by_minute?.[interval] || 0
      }))
    };
  };
  const goalsByInterval = parseGoalsByInterval(teamStats.home, teamStats.away);

  // Calculate max goals for scaling bars
  const maxGoalsInIntervals = Math.max(1, ...goalsByInterval.home.flatMap(i => [i.goalsFor, i.goalsAgainst]), ...goalsByInterval.away.flatMap(i => [i.goalsFor, i.goalsAgainst]));

  // Helper function to determine background color
  const getIntervalBgColor = (goalsFor: number, goalsAgainst: number) => {
    if (goalsFor > goalsAgainst) return 'bg-green-50';
    if (goalsAgainst > goalsFor) return 'bg-red-50';
    return 'bg-gray-50';
  };

  // --- Cards Timing helpers ---
  const parseCardsByInterval = (homeStats: any, awayStats: any) => {
    if (!homeStats || !awayStats) return { home: [], away: [] };
    const intervals = ['0-15', '16-30', '31-45', '46-60', '61-75', '76-90'];
    return {
      home: intervals.map(interval => ({
        interval,
        yellowCards: homeStats.yellow_cards_by_minute?.[interval] || 0,
        redCards: homeStats.red_cards_by_minute?.[interval] || 0
      })),
      away: intervals.map(interval => ({
        interval,
        yellowCards: awayStats.yellow_cards_by_minute?.[interval] || 0,
        redCards: awayStats.red_cards_by_minute?.[interval] || 0
      }))
    };
  };

  const getCardIntervalBgColor = (yellow: number, red: number) => {
    const total = yellow + red;
    if (total === 0) return 'bg-green-50';
    if (total === 1) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  const calculateTotalCards = (cardsByMinute: any): number => {
    if (!cardsByMinute) return 0;
    return Object.values(cardsByMinute).reduce<number>((sum: number, val: any) => sum + (Number(val) || 0), 0);
  };

  const calculateCardStats = (ts: any) => {
    const totalYellow = calculateTotalCards(ts.yellow_cards_by_minute);
    const totalRed = calculateTotalCards(ts.red_cards_by_minute);
    const totalCards = totalYellow + totalRed;
    const gamesPlayed = ts.league_fixtures_played_total || 0;
    const avgCards = gamesPlayed > 0 ? totalCards / gamesPlayed : 0;
    return { totalYellow, totalRed, totalCards, avgCards, gamesPlayed };
  };

  const cardsByInterval = parseCardsByInterval(teamStats.home, teamStats.away);
  const homeCardStats = teamStats.home ? calculateCardStats(teamStats.home) : null;
  const awayCardStats = teamStats.away ? calculateCardStats(teamStats.away) : null;
  const maxCardsInIntervals = Math.max(1, ...cardsByInterval.home.flatMap(i => [i.yellowCards, i.redCards]), ...cardsByInterval.away.flatMap(i => [i.yellowCards, i.redCards]));



  // H2H stats - da perspetiva das equipas ATUAIS
  const currentHomeTeamId = teamStats.home?.team_api_id;
  const currentAwayTeamId = teamStats.away?.team_api_id;

  const h2hStats = {
    homeWins: 0,
    draws: 0,
    awayWins: 0,
    total: h2h.length
  };

  h2h.forEach(match => {
    const homeScore = match.home_goals || 0;
    const awayScore = match.away_goals || 0;
    
    if (homeScore === awayScore) {
      h2hStats.draws++;
      return;
    }
    
    // Determinar quem ganhou este jogo histórico
    const winnerTeamId = homeScore > awayScore ? match.home_team_id : match.away_team_id;
    
    // Contar vitórias da perspetiva das equipas ATUAIS
    if (winnerTeamId === currentHomeTeamId) {
      h2hStats.homeWins++;
    } else if (winnerTeamId === currentAwayTeamId) {
      h2hStats.awayWins++;
    }
  });

  // Calcular percentagens
  const homeWinPercentage = h2hStats.total > 0 
    ? Math.round((h2hStats.homeWins / h2hStats.total) * 100) 
    : 0;
  const drawPercentage = h2hStats.total > 0 
    ? Math.round((h2hStats.draws / h2hStats.total) * 100) 
    : 0;
  const awayWinPercentage = h2hStats.total > 0 
    ? Math.round((h2hStats.awayWins / h2hStats.total) * 100) 
    : 0;

  // Parse percentage string like "38%" to number 38
  const parsePercent = (val: string | number | null): number => {
    if (val == null) return 0;
    if (typeof val === 'number') return val;
    return parseFloat(val.replace('%', '')) || 0;
  };

  // Normalize two values so they sum to 100% for proportional bar fill
  const normalizeBar = (home: string | number | null, away: string | number | null) => {
    const h = parsePercent(home);
    const a = parsePercent(away);
    const total = h + a;
    if (total === 0) return { homeWidth: 50, awayWidth: 50 };
    return { homeWidth: (h / total) * 100, awayWidth: (a / total) * 100 };
  };

  // Helper function to get competitive colors - higher is always better
  const getCompetitiveColors = (
    homeValue: number | string | null, 
    awayValue: number | string | null
  ) => {
    const home = typeof homeValue === 'string' ? parseFloat(homeValue) : (homeValue || 0);
    const away = typeof awayValue === 'string' ? parseFloat(awayValue) : (awayValue || 0);
    
    // If equal, both yellow
    if (home === away) {
      return {
        homeColor: 'bg-yellow-400',
        awayColor: 'bg-yellow-400'
      };
    }
    
    // Higher value is always better (consistent logic)
    return {
      homeColor: home > away ? 'bg-green-500' : 'bg-red-500',
      awayColor: away > home ? 'bg-green-500' : 'bg-red-500'
    };
  };

  // Helper to extract team logos from H2H raw_json
  const extractTeamLogos = (match: any) => {
    try {
      const homeLogo = match.raw_json?.teams?.home?.logo || null;
      const awayLogo = match.raw_json?.teams?.away?.logo || null;
      return { homeLogo, awayLogo };
    } catch {
      return { homeLogo: null, awayLogo: null };
    }
  };

  // Helper to identify relevant trends (>= 60%) to highlight
  type TrendData = {
    label: string;
    percentage: number;
    count: string;
    color: 'green' | 'yellow' | 'red';
    type: 'over' | 'under';
  };

  const getRelevantTrends = (data: any): TrendData[] => {
    if (!data) return [];
    
    const trends: TrendData[] = [];
    
    // Only relevant thresholds: 0.5, 1.5, 2.5
    ['0.5', '1.5', '2.5'].forEach(threshold => {
      const forData = data.goalsFor.find((d: any) => d.threshold === threshold);
      const againstData = data.goalsAgainst.find((d: any) => d.threshold === threshold);
      
      if (forData) {
        const total = forData.over + forData.under;
        if (total > 0) {
          const overPercent = (forData.over / total) * 100;
          
          if (overPercent >= 60) {
            trends.push({
              label: `Over ${threshold} Scored`,
              percentage: overPercent,
              count: `${forData.over}/${total}`,
              color: overPercent >= 80 ? 'green' : 'yellow',
              type: 'over'
            });
          }
        }
      }
      
      if (againstData) {
        const total = againstData.over + againstData.under;
        if (total > 0) {
          const overPercent = (againstData.over / total) * 100;
          
          if (overPercent >= 60) {
            trends.push({
              label: `Over ${threshold} Conceded`,
              percentage: overPercent,
              count: `${againstData.over}/${total}`,
              color: overPercent >= 80 ? 'green' : 'yellow',
              type: 'over'
            });
          }
        }
      }
    });

    return trends.sort((a, b) => b.percentage - a.percentage).slice(0, 6);
  };

  // Helper function to parse under/over data from raw_json
  const parseUnderOverData = (rawJson: any) => {
    if (!rawJson?.league?.goals?.for?.under_over || !rawJson?.league?.goals?.against?.under_over) {
      return null;
    }

    const goalsFor = rawJson.league.goals.for.under_over;
    const goalsAgainst = rawJson.league.goals.against.under_over;

    // Only relevant thresholds: 0.5, 1.5, 2.5, 3.5
    const thresholds = ['0.5', '1.5', '2.5', '3.5'];
    
    const result = {
      goalsFor: thresholds
        .map(t => ({
          threshold: t,
          over: goalsFor[t]?.over || 0,
          under: goalsFor[t]?.under || 0,
        }))
        .filter(item => (item.over + item.under) > 0), // Remove thresholds with no games
      goalsAgainst: thresholds
        .map(t => ({
          threshold: t,
          over: goalsAgainst[t]?.over || 0,
          under: goalsAgainst[t]?.under || 0,
        }))
        .filter(item => (item.over + item.under) > 0), // Remove thresholds with no games
    };

    return result;
  };

  // Parse under/over data from raw_json
  const homeUnderOver = teamStats.home?.raw_json 
    ? parseUnderOverData(teamStats.home.raw_json) 
    : null;

  const awayUnderOver = teamStats.away?.raw_json 
    ? parseUnderOverData(teamStats.away.raw_json) 
    : null;

  return <div className="space-y-6">
      {/* SG7 Analysis Card - First */}
      <SG7PredictionCard
        fixtureId={fixtureId}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
        homeTeamLogo={homeTeamLogo}
        awayTeamLogo={awayTeamLogo}
        leagueName={leagueName}
        leagueLogo={leagueLogo}
        matchDate={matchDate}
        gameStatus={gameStatus}
        homeScore={homeScore}
        awayScore={awayScore}
        elapsed={elapsed}
        statusShort={statusShort}
      />

      {/* 2. Key Facts */}
      <KeyFactsCard
        keyFacts={analysisData?.ai_output?.sidebars?.key_facts}
        safeNumbers={analysisData?.safe_numbers}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
        homeTeamLogo={homeTeamLogo}
        awayTeamLogo={awayTeamLogo}
      />

      {/* Fallback notice: stats from previous season or a different competition */}
      {(() => {
        const src = teamStats.home?.fallback_source || teamStats.away?.fallback_source;
        if (!src) return null;
        // Format: "qualifiers:<leagueId>:<season>"
        const parts = String(src).split(':');
        const fbLeagueId = Number(parts[1]);
        const sameLeague = leagueApiId && fbLeagueId && fbLeagueId === leagueApiId;
        const message = sameLeague
          ? 'Estatísticas baseadas na época anterior desta competição (a nova época ainda não começou).'
          : 'Estatísticas baseadas na competição mais recente de cada equipa (esta competição ainda não tem dados).';
        return (
          <div className="text-[11px] uppercase tracking-wide font-bold text-amber-500/90 bg-amber-500/5 border border-amber-500/20 rounded-md px-3 py-2">
            {message}
          </div>
        );
      })()}

      {/* 3. Form & Performance Comparison */}
      {comparison && <Card className="sketch-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-primary" />
              <CardTitle className="text-sketch-md">Form and Performance</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Form */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs text-gray-medium">
                <div className="flex items-center gap-1.5">
                  {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-4 h-4" />}
                  <span className="font-black">{comparison.form_home || 'N/A'}</span>
                </div>
                <span className="font-bold">FORM</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-black">{comparison.form_away || 'N/A'}</span>
                  {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-4 h-4" />}
                </div>
              </div>
              {(() => {
                const { homeWidth, awayWidth } = normalizeBar(comparison.form_home, comparison.form_away);
                return (
                  <div className="relative h-4 bg-muted rounded-full overflow-hidden flex">
                    <div 
                      className={`h-full transition-all ${getCompetitiveColors(comparison.form_home, comparison.form_away).homeColor}`} 
                      style={{ width: `${homeWidth}%` }} 
                    />
                    <div 
                      className={`h-full transition-all ${getCompetitiveColors(comparison.form_home, comparison.form_away).awayColor}`} 
                      style={{ width: `${awayWidth}%` }} 
                    />
                  </div>
                );
              })()}
            </div>

            {/* Attack */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs text-gray-medium">
                <div className="flex items-center gap-1.5">
                  {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-4 h-4" />}
                  <span className="font-black">{comparison.att_home || 'N/A'}</span>
                </div>
                <span className="font-bold">ATTACK</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-black">{comparison.att_away || 'N/A'}</span>
                  {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-4 h-4" />}
                </div>
              </div>
              {(() => {
                const { homeWidth, awayWidth } = normalizeBar(comparison.att_home, comparison.att_away);
                return (
                  <div className="relative h-4 bg-muted rounded-full overflow-hidden flex">
                    <div 
                      className={`h-full transition-all ${getCompetitiveColors(comparison.att_home, comparison.att_away).homeColor}`} 
                      style={{ width: `${homeWidth}%` }} 
                    />
                    <div 
                      className={`h-full transition-all ${getCompetitiveColors(comparison.att_home, comparison.att_away).awayColor}`} 
                      style={{ width: `${awayWidth}%` }} 
                    />
                  </div>
                );
              })()}
            </div>

            {/* Defense */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs text-gray-medium">
                <div className="flex items-center gap-1.5">
                  {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-4 h-4" />}
                  <span className="font-black">{comparison.def_home || 'N/A'}</span>
                </div>
                <span className="font-bold">DEFENSE</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-black">{comparison.def_away || 'N/A'}</span>
                  {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-4 h-4" />}
                </div>
              </div>
              {(() => {
                const { homeWidth, awayWidth } = normalizeBar(comparison.def_home, comparison.def_away);
                return (
                  <div className="relative h-4 bg-muted rounded-full overflow-hidden flex">
                    <div 
                      className={`h-full transition-all ${getCompetitiveColors(comparison.def_home, comparison.def_away).homeColor}`} 
                      style={{ width: `${homeWidth}%` }} 
                    />
                    <div 
                      className={`h-full transition-all ${getCompetitiveColors(comparison.def_home, comparison.def_away).awayColor}`} 
                      style={{ width: `${awayWidth}%` }} 
                    />
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>}

      {/* Recent Games */}
      {(teamHomeId || teamAwayId) && (
        <Card className="sketch-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-primary" />
              <CardTitle className="text-sketch-md">Recent Games</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:divide-x md:divide-border">
              <div className="md:pr-3">
                {teamHomeId && (
                  <TeamRecentGamesDisplay
                    teamId={teamHomeId}
                    teamName={homeTeamName}
                    teamLogo={homeTeamLogo}
                    location="all"
                  />
                )}
              </div>
              <div className="md:pl-3">
                {teamAwayId && (
                  <TeamRecentGamesDisplay
                    teamId={teamAwayId}
                    teamName={awayTeamName}
                    teamLogo={awayTeamLogo}
                    location="all"
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3. Team Statistics Grid */}
      {(teamStats.home || teamStats.away) && <div className="grid md:grid-cols-2 gap-4">
          {/* Home Team */}
          {teamStats.home && <Card className="sketch-border bg-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-6 h-6" />}
                  <CardTitle className="text-sm">{homeTeamName}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <div>
                  <div className="font-bold mb-1">Last 5 Games</div>
                  <div className="bg-paper-sketch p-2 sketch-border">
                    <div className="space-y-1">
                      <span className="text-muted-foreground">Form:</span>
                      {renderFormBoxes(teamStats.home.last_5_form)}
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Scored:</span>
                      <span className="font-black">{teamStats.home.last_5_goals_for_total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Conceded:</span>
                      <span className="font-black">{teamStats.home.last_5_goals_against_total || 0}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="font-bold mb-1">League (Home)</div>
                  <div className="bg-paper-sketch p-2 sketch-border">
                    <div className="flex justify-between">
                      <span>Games:</span>
                      <span className="font-black">{teamStats.home.league_fixtures_played_home || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Wins:</span>
                      <span className="font-black">{teamStats.home.league_fixtures_wins_home || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Draws:</span>
                      <span className="font-black">{teamStats.home.league_fixtures_draws_home || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Losses:</span>
                      <span className="font-black">{teamStats.home.league_fixtures_loses_home || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Scored:</span>
                      <span className="font-black">{teamStats.home.league_goals_for_total_home || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Conceded:</span>
                      <span className="font-black">{teamStats.home.league_goals_against_total_home || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Badge variant="secondary" className="text-xs">
                    🛡️ Clean Sheets: {teamStats.home.clean_sheet_home || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>}

          {/* Away Team */}
          {teamStats.away && <Card className="sketch-border bg-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-6 h-6" />}
                  <CardTitle className="text-sm">{awayTeamName}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <div>
                  <div className="font-bold mb-1">Last 5 Games</div>
                  <div className="bg-paper-sketch p-2 sketch-border">
                    <div className="space-y-1">
                      <span className="text-muted-foreground">Form:</span>
                      {renderFormBoxes(teamStats.away.last_5_form)}
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Scored:</span>
                      <span className="font-black">{teamStats.away.last_5_goals_for_total || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Conceded:</span>
                      <span className="font-black">{teamStats.away.last_5_goals_against_total || 0}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="font-bold mb-1">League (Away)</div>
                  <div className="bg-paper-sketch p-2 sketch-border">
                    <div className="flex justify-between">
                      <span>Games:</span>
                      <span className="font-black">{teamStats.away.league_fixtures_played_away || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Wins:</span>
                      <span className="font-black">{teamStats.away.league_fixtures_wins_away || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Draws:</span>
                      <span className="font-black">{teamStats.away.league_fixtures_draws_away || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Losses:</span>
                      <span className="font-black">{teamStats.away.league_fixtures_loses_away || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Scored:</span>
                      <span className="font-black">{teamStats.away.league_goals_for_total_away || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Goals Conceded:</span>
                      <span className="font-black">{teamStats.away.league_goals_against_total_away || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Badge variant="secondary" className="text-xs">
                    🛡️ Clean Sheets: {teamStats.away.clean_sheet_away || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>}
        </div>}

      {/* 4. Tabela Estatística Detalhada */}
      {teamStats.home && teamStats.away && <Card className="sketch-border bg-card overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-primary" />
              <CardTitle className="text-sketch-md">Comparative Statistics</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <PredictionStatsTable homeStats={teamStats.home} awayStats={teamStats.away} homeTeamName={homeTeamName} awayTeamName={awayTeamName} homeTeamLogo={homeTeamLogo} awayTeamLogo={awayTeamLogo} />
          </CardContent>
        </Card>}

      {/* 5. Análise Under/Over - Debug Fallback */}
      {(!homeUnderOver || !awayUnderOver) && (
        <Card className="sketch-border bg-card border-yellow-500/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <TrendingDown className="w-5 h-5 text-yellow-500" />
              <CardTitle className="text-sketch-md text-yellow-500">
                ⚠️ Under/Over data not available
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                Under/Over analysis data is not available for this game.
              </p>
              <div className="bg-muted/50 p-4 rounded-md space-y-2 font-mono text-xs">
                <p className="font-semibold text-foreground">🔍 Debug Info:</p>
                <p>• Home Team raw_json: {teamStats.home?.raw_json ? '✅ Exists' : '❌ Missing'}</p>
                <p>• Away Team raw_json: {teamStats.away?.raw_json ? '✅ Exists' : '❌ Missing'}</p>
                <p>• Home Under/Over processed: {homeUnderOver ? '✅ Yes' : '❌ No'}</p>
                <p>• Away Under/Over processed: {awayUnderOver ? '✅ Yes' : '❌ No'}</p>
              </div>
              <p className="text-muted-foreground">
                💡 Tip: Click the "Fetch Predictions" button above to load complete data.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 5. Tendências Over/Under */}
      {homeUnderOver && awayUnderOver && (
        <Card className="sketch-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <TrendingDown className="w-5 h-5 text-primary" />
              <CardTitle className="text-sketch-md">Over/Under Trends</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Most relevant statistics from recent games
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Casa */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-bold mb-3 pb-2 border-b">
                  {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5" />}
                  <span>{homeTeamName}</span>
                </div>
                
                {getRelevantTrends(homeUnderOver).length > 0 ? (
                  <div className="space-y-2">
                    {getRelevantTrends(homeUnderOver).map((trend, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg ${
                          trend.color === 'green' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                          trend.color === 'yellow' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                          'bg-red-500/10 border border-red-500/30'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <p className="text-2xl font-black">
                              {trend.percentage.toFixed(0)}%
                            </p>
                            <p className="text-xs text-muted-foreground">{trend.label}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">{trend.count}</p>
                            <p className="text-xs text-muted-foreground">Games</p>
                          </div>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              trend.color === 'green' ? 'bg-emerald-500' :
                              trend.color === 'yellow' ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${trend.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No strong trends (&gt;60%)
                  </p>
                )}
              </div>

              {/* Fora */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-bold mb-3 pb-2 border-b">
                  {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5" />}
                  <span>{awayTeamName}</span>
                </div>
                
                {getRelevantTrends(awayUnderOver).length > 0 ? (
                  <div className="space-y-2">
                    {getRelevantTrends(awayUnderOver).map((trend, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg ${
                          trend.color === 'green' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                          trend.color === 'yellow' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                          'bg-red-500/10 border border-red-500/30'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <p className="text-2xl font-black">
                              {trend.percentage.toFixed(0)}%
                            </p>
                            <p className="text-xs text-muted-foreground">{trend.label}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">{trend.count}</p>
                            <p className="text-xs text-muted-foreground">Games</p>
                          </div>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              trend.color === 'green' ? 'bg-emerald-500' :
                              trend.color === 'yellow' ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${trend.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No strong trends (&gt;60%)
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 6. Momento dos Golos */}
      {teamStats.home && teamStats.away && <Card className="sketch-border bg-card overflow-hidden">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-primary" />
              <CardTitle className="text-sketch-md">Goal Timing</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Coluna Casa */}
              <div className="space-y-3">
                {/* Header Casa */}
                <div className="flex items-center justify-center gap-2 pb-2 border-b">
                  {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5" />}
                  <span className="font-black text-sm">{homeTeamName}</span>
                </div>

                {/* Intervalos Casa */}
                {goalsByInterval.home.map(interval => <div key={`home-${interval.interval}`} className={`${getIntervalBgColor(interval.goalsFor, interval.goalsAgainst)} p-3 rounded-lg space-y-2`}>
                    {/* Label do Intervalo */}
                    <div className="font-black text-sm text-foreground">{interval.interval}'</div>
                    
                    {/* G.Sc - Barra Verde */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14 text-muted-foreground">G.Sc</span>
                      <span className="text-xs font-black w-6 text-foreground">{interval.goalsFor}</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-600 transition-all" style={{
                    width: `${interval.goalsFor / maxGoalsInIntervals * 100}%`
                  }} />
                      </div>
                    </div>
                    
                    {/* G.Con - Barra Vermelha */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14 text-muted-foreground">G.Con</span>
                      <span className="text-xs font-black w-6 text-foreground">{interval.goalsAgainst}</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-red-600 transition-all" style={{
                    width: `${interval.goalsAgainst / maxGoalsInIntervals * 100}%`
                  }} />
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Coluna Fora */}
              <div className="space-y-3">
                {/* Header Fora */}
                <div className="flex items-center justify-center gap-2 pb-2 border-b">
                  {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5" />}
                  <span className="font-black text-sm">{awayTeamName}</span>
                </div>

                {/* Intervalos Fora */}
                {goalsByInterval.away.map(interval => <div key={`away-${interval.interval}`} className={`${getIntervalBgColor(interval.goalsFor, interval.goalsAgainst)} p-3 rounded-lg space-y-2`}>
                    {/* Label do Intervalo */}
                    <div className="font-black text-sm text-foreground">{interval.interval}'</div>
                    
                    {/* G.Sc - Barra Verde */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14 text-muted-foreground">G.Sc</span>
                      <span className="text-xs font-black w-6 text-foreground">{interval.goalsFor}</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-600 transition-all" style={{
                    width: `${interval.goalsFor / maxGoalsInIntervals * 100}%`
                  }} />
                      </div>
                    </div>
                    
                    {/* G.Con - Barra Vermelha */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-14 text-muted-foreground">G.Con</span>
                      <span className="text-xs font-black w-6 text-foreground">{interval.goalsAgainst}</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-red-600 transition-all" style={{
                    width: `${interval.goalsAgainst / maxGoalsInIntervals * 100}%`
                  }} />
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Nota sobre descontos */}
            <p className="text-xs text-muted-foreground text-center italic mt-4">
              * Periods 31-45 and 76-90 include injury time
            </p>
          </CardContent>
        </Card>}

      {/* Cards Timing */}
      {teamStats.home && teamStats.away && homeCardStats && awayCardStats && cardsByInterval.home.length > 0 && <Card className="sketch-border bg-card overflow-hidden">
        <CardHeader>
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-yellow-600" />
            <CardTitle className="text-sketch-md">Cards Timing</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* Card Stats Summary */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Home Stats */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-4 rounded-lg sketch-border overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm truncate">{homeTeamName}</span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-muted-foreground">Total:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">🟨 {homeCardStats.totalYellow}</span>
                  <span className="text-sm font-bold">🟥 {homeCardStats.totalRed}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-xs text-muted-foreground">Avg:</span>
                <span className="text-lg font-black text-primary leading-tight">{homeCardStats.avgCards.toFixed(2)}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">cards/game</span>
              </div>
              <div className="text-[10px] text-muted-foreground/70 mt-2">{homeCardStats.gamesPlayed} games played</div>
            </div>
            {/* Away Stats */}
            <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 p-4 rounded-lg sketch-border overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm truncate">{awayTeamName}</span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-muted-foreground">Total:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">🟨 {awayCardStats.totalYellow}</span>
                  <span className="text-sm font-bold">🟥 {awayCardStats.totalRed}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-xs text-muted-foreground">Avg:</span>
                <span className="text-lg font-black text-foreground leading-tight">{awayCardStats.avgCards.toFixed(2)}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">cards/game</span>
              </div>
              <div className="text-[10px] text-muted-foreground/70 mt-2">{awayCardStats.gamesPlayed} games played</div>
            </div>
          </div>

          <div className="border-t border-dashed border-border mb-6" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Home Column */}
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 pb-2 border-b">
                {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm">{homeTeamName}</span>
              </div>
              {cardsByInterval.home.map(interval => (
                <div key={`home-cards-${interval.interval}`} className={`${getCardIntervalBgColor(interval.yellowCards, interval.redCards)} p-3 rounded-lg space-y-2`}>
                  <div className="font-black text-sm text-foreground">{interval.interval}'</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟨 Yel</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.yellowCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 transition-all" style={{ width: `${interval.yellowCards / maxCardsInIntervals * 100}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟥 Red</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.redCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-600 transition-all" style={{ width: `${interval.redCards / maxCardsInIntervals * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Away Column */}
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 pb-2 border-b">
                {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5" />}
                <span className="font-black text-sm">{awayTeamName}</span>
              </div>
              {cardsByInterval.away.map(interval => (
                <div key={`away-cards-${interval.interval}`} className={`${getCardIntervalBgColor(interval.yellowCards, interval.redCards)} p-3 rounded-lg space-y-2`}>
                  <div className="font-black text-sm text-foreground">{interval.interval}'</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟨 Yel</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.yellowCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 transition-all" style={{ width: `${interval.yellowCards / maxCardsInIntervals * 100}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-14 text-muted-foreground">🟥 Red</span>
                    <span className="text-xs font-black w-6 text-foreground">{interval.redCards}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-600 transition-all" style={{ width: `${interval.redCards / maxCardsInIntervals * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center italic mt-4">
            * Periods 31-45 and 76-90 include injury time
          </p>
        </CardContent>
      </Card>}

      {/* 8. H2H History */}
      {h2h.length > 0 && <Card className="sketch-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-primary" />
              <CardTitle className="text-sketch-md">Head to Head</CardTitle>
            </div>
            <CardDescription className="text-xs text-muted-foreground">
              Last {h2h.length} games between teams
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3 pb-4">
            {/* Visual H2H Stats */}
            <div className="space-y-3">
              {/* Home Team Wins */}
              <div className="space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-bold uppercase tracking-wide">
                    {homeTeamName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {h2hStats.homeWins} {h2hStats.homeWins === 1 ? 'Win' : 'Wins'} ({homeWinPercentage}%)
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${homeWinPercentage}%` }}
                  />
                </div>
              </div>

              {/* Draws */}
              <div className="space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-muted-foreground">
                    Draws
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {h2hStats.draws} ({drawPercentage}%)
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-400 transition-all duration-500"
                    style={{ width: `${drawPercentage}%` }}
                  />
                </div>
              </div>

              {/* Away Team Wins */}
              <div className="space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-bold uppercase tracking-wide">
                    {awayTeamName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {h2hStats.awayWins} {h2hStats.awayWins === 1 ? 'Win' : 'Wins'} ({awayWinPercentage}%)
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-red-500 transition-all duration-500"
                    style={{ width: `${awayWinPercentage}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t my-4" />

            {/* Recent Matches List */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground">Recent Matches</h4>
            {h2h.slice(0, 5).map(match => {
              const { homeLogo, awayLogo } = extractTeamLogos(match);
              
              return (
                <button
                  key={match.id}
                  onClick={() => {
                    if (currentGameId) {
                      sessionStorage.setItem('returnContext', JSON.stringify({
                        game: currentGameId,
                        tab: 'predictions'
                      }));
                    }
                    navigate(`/game/${match.fixture_id}`);
                  }}
                  className="w-full flex justify-between items-center p-2 bg-paper-sketch sketch-border text-xs hover:bg-accent/50 transition-colors cursor-pointer group"
                >
                  <span className="text-gray-medium">
                    {match.match_date ? format(new Date(match.match_date), 'dd/MM/yyyy') : 'N/A'}
                  </span>
                  
                  {/* Home Team + Goals VS Goals + Away Team */}
                  <div className="flex items-center gap-2 font-black">
                    {/* Home: Logo + 3 Letters + Goals */}
                    <div className="flex items-center gap-1">
                      {homeLogo && (
                        <img 
                          src={homeLogo} 
                          alt={match.home_team_name || ''} 
                          className="w-4 h-4 object-contain"
                        />
                      )}
                      <span>{match.home_team_name?.substring(0, 3).toUpperCase()}</span>
                      <span className="text-foreground">{match.home_goals ?? 0}</span>
                    </div>
                    
                    {/* VS Separator */}
                    <span className="text-muted-foreground font-normal">-</span>
                    
                    {/* Away: Goals + 3 Letters + Logo */}
                    <div className="flex items-center gap-1">
                      <span className="text-foreground">{match.away_goals ?? 0}</span>
                      <span>{match.away_team_name?.substring(0, 3).toUpperCase()}</span>
                      {awayLogo && (
                        <img 
                          src={awayLogo} 
                          alt={match.away_team_name || ''} 
                          className="w-4 h-4 object-contain"
                        />
                      )}
                    </div>
                    
                    {/* External Link Icon (shows on hover) */}
                    <ExternalLink className="w-3 h-3 ml-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              );
            })}
            </div>
          </CardContent>
        </Card>}

    </div>;
}